import {
  useCreateTourFromUrlMutation,
  useLazyGetDemoQuery,
  useLazyGetOrganizationQuery,
  useLazyGetPlaybookQuery,
} from '@carboncopyco/data';
import type { Tour } from '@carboncopyco/types';
import {
  type GlobalTabType,
  type PageDetails,
  TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';
import type { UserPersonas } from '@carboncopyco/types/desktop';
import { useEffect } from 'react';
import type { ExternalCloneSession } from '../../shared/cloner/types';
import { AppEventsEnum } from '../../shared/enums/AppEventsEnum';
import { UserEventsEnum } from '../../shared/enums/UserEventsEnum';
import { convertKeysToSnakeCase } from '../../util/newObjectSystemHelper';
import { generateGroupId } from '../../util/tab-util';
import useElectronListener from '../hooks/useElectronListener';
import { useFocusAppService } from '../hooks/useFocusAppService';
import { useRestart } from '../hooks/useRestart';
import { useOpenDemoEditorTab } from '../screens/utils/openDemoEditorTab';
import { useAppService } from '../services/AppService';
import { useTabManager } from '../services/TabManager';
import { useClonerService } from '../services/cloner/ClonerService';
import { useUser } from '../store/context';

type DeeplinkUrlData = {
  route: string;
  params: {
    [key: string]: string;
  };
};

const ProtectedEventListener = ({ children }: { children: JSX.Element }) => {
  const { createInnerTab, createGlobalTab } = useTabManager();
  const { updateClone } = useClonerService();
  const { activeOrganization, setActiveOrganization, setPersonas } = useUser();
  const [getDemo] = useLazyGetDemoQuery();
  const [getOrganization] = useLazyGetOrganizationQuery();
  const [createTourFromUrl] = useCreateTourFromUrlMutation();
  const [getPlaybook] = useLazyGetPlaybookQuery();
  const { restartApp } = useRestart();
  const { hideOnSwitchFocus } = useFocusAppService();

  const { startClone } = useAppService();
  const openDemoEditorTab = useOpenDemoEditorTab();

  const updateCloneReceived = async (data: any) => {
    const { organizationId, recordingId } = data;
    if (!organizationId || !recordingId) return;
    updateClone(organizationId, recordingId);
  };

  const newInnerTab = (...args: unknown[]) => {
    createInnerTab(args[0] as PageDetails);
  };

  const setPersonasChanged = (...args: unknown[]) => {
    setPersonas(args[0] as UserPersonas);
  };

  const setupOrganization = (organizationId: string) => {
    if (organizationId !== activeOrganization?.id?.toString()) {
      getOrganization({ id: organizationId })
        .then(({ data }) => {
          return setActiveOrganization(
            convertKeysToSnakeCase(
              data as {
                [key: string]: any;
              }
            )
          );
        })
        .catch((error) => {
          throw new Error(error);
        });
    }
  };

  const handleOpenEditor = async (...args: unknown[]) => {
    hideOnSwitchFocus();
    const { params } = args[0] as DeeplinkUrlData;

    const demo = await getDemo({
      workspaceId: params.workspaceId,
      organizationId: params.organizationId,
      id: params.demoId,
      removeDeleted: true,
    }).unwrap();
    openDemoEditorTab(demo, activeOrganization.id);
  };

  const handleChangeOrganization = (...args: unknown[]) => {
    const organizationId = args[0] as string;
    setupOrganization(organizationId);
    restartApp();
  };

  const handleOpenCloner = (...args: unknown[]) => {
    const externalCloneSession = args[0] as ExternalCloneSession;
    startClone({ externalCloneSession });
  };

  const handleDeeplinkTabOpen = async (...args: unknown[]) => {
    const { params } = args[0] as DeeplinkUrlData;
    const {
      assetId,
      assetType,
      organizationId,
      workspaceId,
      assetRevisionId,
      withGiveADemo,
      hidePlayLater,
    } = params;
    if (!assetId || !assetType || !organizationId) return;
    let data;
    let tabType;
    if (assetType === 'demo') {
      data = await getDemo({
        workspaceId,
        organizationId,
        id: assetId,
        removeDeleted: true,
      }).unwrap();
      tabType = TabType.PRESENTER_DEEPLINK_DEMO;
    } else if (assetType === 'playbook') {
      data = await getPlaybook({
        workspaceId,
        organizationId,
        id: assetId,
      }).unwrap();
      tabType = TabType.PRESENTER_DEEPLINK_PLAYBOOK;
    }
    if (data && tabType) {
      createGlobalTab({
        type: tabType,
        url: '',
        organizationId,
        workspaceId,
        payload: {
          asset: data,
          assetRevisionId,
          withGiveADemo: withGiveADemo !== 'false',
          hidePlayLater: hidePlayLater === 'true',
        },
        title: 'Untitled demo',
        group: {
          id: generateGroupId(Number(assetId)),
          name: TabGroupName.Deeplink,
          isVisibleIfGroupIsActive: true,
          isVisibleIfGroupIsInactive: true,
          showNonRelatedGroupTabsIfActive: false,
          focusOnCreate: true,
          closeGroupOnClose: true,
        },
      });
    }
  };

  const handleCreateTour = (...args: unknown[]) => {
    const {
      route,
      params: { appUrl, originalTourId, insertIndex, width, height, type },
    } = args[0] as DeeplinkUrlData;
    const [, organizationId, , workspaceId, entityType, demoId] =
      route.split('/');
    setupOrganization(organizationId);

    if (entityType === 'tour') {
      createTourFromUrl({
        organizationId,
        url: appUrl,
        description: 'Tour description',
        tourName: 'Tour name',
        source: 'desktop',
      })
        .then((response) => {
          if ('data' in response) {
            createGlobalTab<GlobalTabType>({
              type: TabType.LIVE_TOURS_CREATE,
              url: appUrl,
              workspaceId,
              organizationId,
              payload: {
                source: {
                  appUrl,
                  playerToken: (
                    response.data as Tour & {
                      playerToken: string;
                    }
                  ).playerToken,
                  fileServiceToken: response.data.fileServiceToken,
                  tourId: response.data.id,
                  ...(originalTourId ? { originalTourId } : {}),
                  ...(insertIndex ? { insertIndex } : {}),
                },
                screenResolution: {
                  width: Number.parseInt(width, 10),
                  height: Number.parseInt(height, 10),
                  type,
                },
              },
              title: 'Untitled Tour',
              createGlobalTabOnElectron: {
                startOn: 'tab-creation',
                withInterceptor: true,
                withWebSecurity: true,
              },
            });
          }
          return null;
        })
        .catch((error) => {
          throw new Error(error);
        });
      return;
    }

    getDemo({
      workspaceId,
      id: demoId,
      organizationId,
    })
      .then((res) => {
        const { data: demo } = res;
        if (!demo) {
          throw new Error('ERROR PAGE - DEMO NOT FOUND');
        }
        return demo;
      })
      .then((demo) => {
        const url = decodeURIComponent(demo.firstUrl);
        return createGlobalTab({
          cloneId: demoId,
          payload: {
            demo,
            forClient: '',
            showWithoutDrafts: false,
            openFromInfoPage: false,
            tourData: {
              originalTourId,
              insertIndex,
            },
            screenResolution: {
              width,
              height,
              type,
            },
          },
          title: demo.demoName,
          hoverTitle: `Tour ${demo.demoName}`,
          url,
          workspaceId,
          organizationId,
          demoId,
          type: TabType.TOURS_CREATE,
        });
      })
      .catch((error) => {
        throw new Error(error);
      });
  };

  const initEvents = () => {
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.UPDATE_CLONE_RECEIVED,
      updateCloneReceived
    );

    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.NEW_INNER_TAB_RECEIVED,
      newInnerTab
    );

    window.dsDesktop?.ipcRenderer.on(
      UserEventsEnum.MAIN_TO_RENDERER_PERSONAS_CHANGED,
      setPersonasChanged
    );

    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.MAIN_TO_APP_CREATE_TOUR,
      handleCreateTour
    );
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.MAIN_TO_APP_CLONER_OPEN,
      handleOpenCloner
    );
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.MAIN_TO_APP_EDITOR_OPEN,
      handleOpenEditor
    );
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.MAIN_TO_APP_CHANGE_ORGANIZATION,
      handleChangeOrganization
    );
  };

  useElectronListener(
    AppEventsEnum.MAIN_TO_APP_PRESENTER_DEEPLINK,
    handleDeeplinkTabOpen
  );

  useEffect(() => {
    initEvents();
  }, []);

  return <>{children}</>;
};

export default ProtectedEventListener;
